/**
 * MFA Verification Dialog Component
 * Used during login when MFA is enabled
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield, Loader2, Key } from 'lucide-react';

export const MFAVerifyDialog = ({
  open,
  onOpenChange,
  onVerify,
  loading = false,
  error = null,
  username = ''
}) => {
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const inputRef = useRef(null);
  
  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);
  
  // Clear code when dialog closes
  useEffect(() => {
    if (!open) {
      setCode('');
      setUseBackupCode(false);
    }
  }, [open]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.length >= 6) {
      onVerify(code, trustDevice);
    }
  };
  
  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (useBackupCode) {
      // Backup codes are 8 characters
      setCode(value.slice(0, 8));
    } else {
      // TOTP codes are 6 digits
      setCode(value.replace(/\D/g, '').slice(0, 6));
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Zwei-Faktor-Authentifizierung
          </DialogTitle>
          <DialogDescription>
            {username && `Anmeldung als ${username}`}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mfa-code">
              {useBackupCode ? 'Backup-Code' : '6-stelliger Code'}
            </Label>
            <Input
              ref={inputRef}
              id="mfa-code"
              type="text"
              inputMode={useBackupCode ? "text" : "numeric"}
              pattern={useBackupCode ? "[A-Za-z0-9]*" : "[0-9]*"}
              maxLength={useBackupCode ? 8 : 6}
              placeholder={useBackupCode ? "XXXXXXXX" : "000000"}
              value={code}
              onChange={handleCodeChange}
              className="text-center text-2xl tracking-widest font-mono"
              autoComplete="one-time-code"
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground text-center">
              {useBackupCode 
                ? 'Geben Sie einen Ihrer Backup-Codes ein'
                : 'Geben Sie den Code aus Ihrer Authenticator-App ein'
              }
            </p>
          </div>
          
          {error && (
            <div className="p-2 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive text-center">
              {error}
            </div>
          )}
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="trust-device"
              checked={trustDevice}
              onCheckedChange={setTrustDevice}
              disabled={loading}
            />
            <Label 
              htmlFor="trust-device" 
              className="text-sm font-normal cursor-pointer"
            >
              Diesem Gerät 30 Tage vertrauen
            </Label>
          </div>
          
          <Button 
            type="submit" 
            className="w-full"
            disabled={loading || code.length < (useBackupCode ? 8 : 6)}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Bestätigen
          </Button>
          
          <button
            type="button"
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setCode('');
            }}
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
          >
            <Key className="w-3 h-3" />
            {useBackupCode ? 'Authenticator-Code verwenden' : 'Backup-Code verwenden'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MFAVerifyDialog;
